import { useConversation } from '@elevenlabs/react';
import { useCallback, useRef, useEffect } from 'react';
import DopaLogo from './DopaLogo';

// Define the type for the ElevenLabs Language.
// This is an example; if ElevenLabs provides a specific enum or type,
// it should be imported or used directly from their library.
type Language = 'en-US' | 'en-GB' | 'es-ES' | 'fr-FR' | 'de-DE' | 'it-IT'; // Example languages supported by ElevenLabs

interface DopaVoiceAgentProps {
  contextSummary: string;
}

const DopaVoiceAgent = ({ contextSummary }: DopaVoiceAgentProps) => {
  // Use a ref to store the contextSummary.
  // This approach is beneficial because the `onConnect` callback (and others)
  // within the `useConversation` hook might not re-run when `contextSummary` changes.
  // By using a ref, the callback can always access the most current `contextSummary`.
  const contextSummaryRef = useRef(contextSummary);

  // Update the ref's current value whenever the `contextSummary` prop changes.
  // This ensures that `contextSummaryRef.current` always holds the latest prop value.
  contextSummaryRef.current = contextSummary;

  // Initialize the ElevenLabs conversation hook.
  const conversationApi = useConversation({
    // Callback function executed when the connection to the ElevenLabs agent is successfully established.
    onConnect: () => {
      console.log('Dopa connected');
      // Note: Context summary will be provided through the agent configuration
      // rather than sending it as a message after connection
    },
    // Callback function executed when the connection to the ElevenLabs agent is disconnected.
    onDisconnect: () => console.log('Dopa disconnected'),
    // Callback function executed when a new message (text or audio) is received from Dopa.
    onMessage: (msg) => console.log('Message:', msg),
    // Callback function executed if an error occurs during the conversation with Dopa.
    onError: (err) => console.error('Error with Dopa:', err),
  });

  // Extract the properties we need from the conversation API
  const {
    status,         // Current connection status (e.g., 'connected', 'connecting', 'disconnected')
    isSpeaking,     // Boolean indicating if Dopa is currently speaking
    startSession,   // Function to start a new conversation session
    endSession      // Function to end the current conversation session
  } = conversationApi;

  // Callback to initiate the voice conversation session.
  // `useCallback` is used to memoize this function, preventing unnecessary re-creations
  // on every render, which optimizes performance.
  const handleStartConversation = useCallback(async () => {
    try {
      // Request permission from the user to access their microphone.
      // This is a browser security feature and is essential for voice input.
      await navigator.mediaDevices.getUserMedia({ audio: true });
      // Start the ElevenLabs session using the specified agent ID.
      // The `agentId` links to your pre-configured voice agent on the ElevenLabs platform.
      await startSession({
        agentId: 'agent_01jypvyad8fgkt2hmsasx93yvf' // Replace with your actual ElevenLabs Agent ID
      });
      // Note: Context summary should be configured in the agent settings on ElevenLabs platform
      // rather than sent as a message after connection
    } catch (err) {
      // Log any errors that occur during the process of starting the session,
      // such as microphone access denied or network issues.
      console.error('Failed to start session:', err);
    }
  }, [startSession]); // Dependency array: ensures the function only re-creates if `startSession` changes.

  // Callback to terminate the voice conversation session.
  // Memoized with `useCallback` for performance.
  const handleStopConversation = useCallback(async () => {
    await endSession();
  }, [endSession]); // Dependency array: ensures the function only re-creates if `endSession` changes.

  // Automatically end session when component unmounts
  useEffect(() => {
    return () => {
      if (status === 'connected') {
        console.log('Cleaning up voice session on unmount');
        endSession().catch(err => {
          console.error('Error ending session on unmount:', err);
        });
      }
    };
  }, [status, endSession]);

  return (
    <div className="bg-peach-50/40 dark:bg-peach-900/30 rounded-3xl border border-peach-100/30 dark:border-peach-700/30 p-8 my-8 max-w-md mx-auto">
      <div className="text-center mb-6">
        <div className="mx-auto mb-4">
          <DopaLogo size="lg" className="justify-center" withText={true} />
        </div>

        <p className="text-peach-600 dark:text-peach-300 font-light text-sm leading-relaxed mb-6">
          Thanks for sharing. You can now talk to Dopa — a gentle voice that listens and supports you without judgment.
        </p>
      </div>

      {/* Control Buttons for starting and stopping the conversation */}
      <div className="flex justify-center space-x-3 mb-6">
        <button
          onClick={handleStartConversation}
          // Button is disabled if the conversation is already connected or in the process of connecting.
          disabled={status === 'connected' || status === 'connecting'}
          className="px-6 py-3 bg-blue-100/60 hover:bg-blue-200/50
                     dark:bg-blue-900/30 dark:hover:bg-blue-800/40
                     text-blue-600 dark:text-blue-300 rounded-2xl text-sm font-light border border-blue-200/40 dark:border-blue-700/40 
                     hover:border-blue-300/50 dark:hover:border-blue-600/50
                     transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-blue-100/50 dark:focus:ring-blue-700/30
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Start Talking
        </button>

        <button
          onClick={handleStopConversation}
          // Button is disabled if the conversation is not currently connected.
          disabled={status !== 'connected'}
          className="px-6 py-3 bg-red-100/60 hover:bg-red-200/50
                     dark:bg-red-900/30 dark:hover:bg-red-800/40
                     text-red-600 dark:text-red-300 rounded-2xl text-sm font-light border border-red-200/40 dark:border-red-700/40 
                     hover:border-red-300/50 dark:hover:border-red-600/50
                     transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-red-100/50 dark:focus:ring-red-700/30
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Stop
        </button>
      </div>

      {/* Status Display of the Dopa AI's current state */}
      <div className="bg-white/60 dark:bg-peach-900/20 rounded-2xl border border-peach-200/30 dark:border-peach-700/30 p-4">
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center space-x-2">
            {/* Visual indicator dot for connection status */}
            <div className={`w-2 h-2 rounded-full ${
              status === 'connected' ? 'bg-green-400' :      // Green if connected
              status === 'connecting' ? 'bg-yellow-400' :    // Yellow if connecting
              'bg-gray-400'                                               // Gray by default (disconnected)
            }`}></div>
            <p className="text-peach-600 dark:text-peach-300 font-light text-xs">
              Status: <span className="font-medium">{status}</span>
            </p>
          </div>

          <p className="text-peach-500 dark:text-peach-400 font-light text-xs">
            Dopa is: <span className="font-medium">
              {/* Display whether Dopa is actively responding or listening, based on `isSpeaking` status */}
              {isSpeaking ? 'responding' : 'listening'}
            </span>
          </p>
        </div>
      </div>

      {/* Helper Text to guide the user */}
      <div className="text-center mt-4">
        <p className="text-peach-400 dark:text-peach-500 text-xs font-light leading-relaxed">
          Speak naturally. Dopa understands your feelings and will respond with care.
        </p>
      </div>
    </div>
  );
};

export default DopaVoiceAgent;